import { toast } from "react-toastify";
import { observable } from "mobx";
import { api } from "../../api";
import { IEmpresa, newEmpresa } from "../../model";
import { Dayjs } from "dayjs";
import { DeiStore } from "../../stores/dei/DeiStore";
import { decodeUtf8String, downloadBinaryFile } from "../../utils";
import { DaeAvulsoStore } from "../dae-avulso/DaeAvulsoStore";

export class _SpedApuracaoStore {
  obs = observable({
    apuracoes: null,
    selectedEmpresaSaida: newEmpresa() as IEmpresa,
    periodoSaida: null as string[],
    dataSaida: null as Dayjs,

    apuracoes_entrada: null,
    selectedEmpresaEntrada: newEmpresa() as IEmpresa,
    periodoEntrada: null as string[],
    dataEntrada: null as Dayjs,
    modelo: "NF-E" as string,

    selectedTab: "apuracao_entrada" as string,

    tasks_relatorio: [],
    page_tasks_relatorio: 1 as number,
    length_tasks_relatorio: 0 as number,
    loading_download: false as boolean,
    loading_solicitacao: false as boolean,
    loading_apuracao: false as boolean,

    apuracoes_dei: null,
    selectedEmpresaDei: newEmpresa() as IEmpresa,
    periodoDei: null as string[],
    dataDei: null as Dayjs

  })

  /**
 * Realiza a busca de apuração de saída para a empresa e período fornecidos.
 * 
 * A função envia uma solicitação para buscar as apurações de saída, com base no CNPJ da empresa e no período selecionado.
 * Caso a resposta seja bem-sucedida, as apurações são armazenadas na propriedade `apuracoes`. Caso contrário, um aviso de erro é exibido.
 * 
 * @param {string} cnpj CNPJ da empresa para a qual a apuração de saída será realizada.
 * @param {string[]} data Período da apuração no formato de array de strings [início, fim].
 * 
 * @returns {Promise<void>} Retorna uma promise que resolve após a busca e o tratamento da resposta.
 */
  async handleBuscaApuracaoSaida(cnpj: string, data: string[]) {

    try {

      let params = {
        cnpj: cnpj,
        period: data,
      }

      let resp = await api.post('/api/sped/apuracao/index', params)

      this.obs.apuracoes = null

      if (resp.data.ok) {

        this.obs.apuracoes = resp.data.apuracoes

        if (!resp.data.apuracoes) {

          toast.warn("Nenhuma apuração encontrada para esse período!", {})

        }

      } else {

        toast.error(resp.data.msg, {})

      }

    } catch (ex) { }

  }

  /**
 * Processa a apuração com os dados fornecidos.
 * 
 * A função envia os dados para o backend e, se o processamento for bem-sucedido, exibe uma mensagem de sucesso.
 * Caso contrário, exibe uma mensagem de erro.
 * 
 * @param {Object} data Dados necessários para realizar o processamento da apuração.
 * 
 * @returns {Promise<boolean>} Retorna `true` se o processamento for bem-sucedido, caso contrário `false`.
 */
  async handleProcessar(data) {

    this.obs.loading_apuracao = true

    let resp = await api.post('/api/sped/apuracao/create', data)

    if (resp.data.ok) {

      this.obs.loading_apuracao = false

      toast.success("Apuração realizada com sucesso!", {})
      return true

    } else {

      this.obs.loading_apuracao = false

      toast.error(resp.data.msg, {})

    }

    this.obs.loading_apuracao = false

    return false

  }

  /**
 * Realiza a busca de apuração de entrada para a empresa, período e modelo fornecidos.
 * 
 * A função envia uma solicitação para buscar as apurações de entrada, com base no CNPJ da empresa, período e modelo. 
 * Ela também processa os dados recebidos e os organiza, incluindo informações adicionais sobre ICMS, FCP e ICMS 1139.
 * 
 * @param {IEmpresa} empresa Objeto representando a empresa que será usada para a busca de apuração de entrada.
 * @param {string[]} period Período da apuração no formato de array de strings [início, fim].
 * @param {string} modelo O modelo da apuração de entrada.
 * 
 * @returns {Promise<void>} Retorna uma promise que resolve após a busca e o processamento dos dados.
 */
  async handleBuscaApuracaoEntrada(empresa: IEmpresa, period: string[], modelo: string) {

    let params = {
      period: period,
      empresa: empresa.cnpj,
      icms_classificacao_id: "TODAS",
      tipo: "Entrada",
      modelo: modelo
    };

    try {

      let resp = await api.post("/api/dae-avulso/summary", { params: params });

      this.obs.apuracoes_entrada = null

      if (resp.data.ok && resp.data.total_summary > 0) {

        const updatedItens = DaeAvulsoStore.formatter_valores(resp)

        this.obs.apuracoes_entrada = [...updatedItens];

      }

    } catch (ex) {

      toast.error(`Erro ao consultar Resultados de Cálculos, tente novamente mais tarde.`, {});

    }

  }

  /**
   * Realiza a busca de apurações DAE/DEI do tipo **Entrada** para a empresa e período informados.
   * 
   * A função envia uma requisição para o endpoint `/api/dae-avulso/summary`, utilizando o CNPJ da empresa, o período e o modelo informados.
   * Em seguida, processa os dados retornados e atualiza a store observável com os valores formatados.
   * Caso não haja resultados, a lista é definida como `null`.
   * 
   * @param {IEmpresa} empresa Objeto representando a empresa para a qual será feita a busca.
   * @param {string[]} period Intervalo de datas no formato `["YYYY-MM-DD", "YYYY-MM-DD"]`, representando o período da apuração.
   * @param {string} modelo Modelo do documento fiscal (ex: `"55"`, `"65"`).
   * 
   * @returns {Promise<void>} Retorna uma `Promise` que se resolve após a conclusão da busca e do processamento dos dados.
  */

  async handleBuscaDei(empresa: IEmpresa, period: string[], modelo: string) {

    let params = {
      period: period,
      empresa: empresa.cnpj,
      icms_classificacao_id: "TODAS",
      tipo: "Entrada",
      modelo: modelo
    };

    try {

      let resp = await api.post("/api/dae-avulso/summary", { params: params });

      this.obs.apuracoes_dei = null

      if (resp.data.ok && resp.data.total_summary > 0) {

        const updatedItens = DeiStore.formatter_valores(resp)

        this.obs.apuracoes_dei = [...updatedItens];

      }

    } catch (ex) {

      toast.error(`Erro ao consultar Resultados de Cálculos, tente novamente mais tarde.`, {});

    }

  }

  /**
 * Realiza a solicitação de exportação da apuração SPED para o backend, com base nos parâmetros fornecidos.
 * 
 * A função envia uma requisição `POST` para o endpoint `/api/sped/apuracao/export`, utilizando os parâmetros informados. 
 * Caso a operação seja bem-sucedida, exibe uma mensagem de sucesso via `toast` e atualiza o estado de carregamento (`loading_solicitacao`). 
 * Em caso de falha, exibe a mensagem de erro retornada pela API ou uma mensagem genérica de falha na exportação.
 * 
 * @param {Record<string, any>} params Objeto contendo os parâmetros necessários para a exportação. 
 * Recomenda-se tipar com uma interface específica (`ExportParams`) para maior segurança.
 * 
 * @returns {Promise<void>} Retorna uma promise que resolve quando o processo de exportação for concluído.
  */

  async export(params): Promise<void> {

    try {

      this.obs.loading_solicitacao = true

      let resp = await api.post("/api/sped/apuracao/export", params);

      if (resp.data.ok) {
        this.obs.loading_solicitacao = false

        toast.success(`Solicitação realizada com sucesso.`, {});

      } else {

        this.obs.loading_solicitacao = false


        toast.error(resp.data.erro, {});

      }

    } catch (ex) {

      this.obs.loading_solicitacao = false

      toast.error(`Erro ao exportar arquivo, tente novamente mais tarde.`, {});

    }

  }

  /**
 * Busca a lista paginada de relatórios de exportação solicitados para a empresa selecionada.
 * 
 * A função envia uma solicitação `POST` para o endpoint `/api/sped/apuracao/export/index`,
 * informando a página solicitada e um limite fixo de 15 itens. Caso haja uma empresa selecionada
 * (`selectedEmpresaSaida`), o CNPJ dela é incluído no filtro.
 * 
 * Ao receber a resposta, a função atualiza os observáveis:
 * - `tasks_relatorio`: lista de relatórios retornados.
 * - `length_tasks_relatorio`: quantidade total de relatórios encontrados.
 * 
 * Em caso de erro na requisição ou no processamento da resposta, é exibida
 * uma mensagem de erro via `toast`.
 * 
 * @async
 * @param {number} paged Número da página que deve ser carregada.
 * 
 * @returns {Promise<void>} Retorna uma Promise que é resolvida após a busca e atualização dos dados.
 */

  async handleTasksPaged(paged: number) {
    try {

      const selectedEmpresa = SpedApuracaoStore.obs.selectedEmpresaSaida

      let data: any = {
        page: paged,
        limit: 15
      };

      if (selectedEmpresa && selectedEmpresa.cnpj) {
        data.cnpj = selectedEmpresa.cnpj;
      }

      let resp = await api.post("/api/sped/apuracao/export/index", data);

      this.obs.tasks_relatorio = [];
      this.obs.length_tasks_relatorio = 0;

      if (resp.data.itens.length > 0) {
        this.obs.tasks_relatorio = resp.data.itens;
        this.obs.length_tasks_relatorio = resp.data.count;
      }

    } catch (ex) {
      toast.error("Erro ao Carregar Relatórios Solicitados, tente novamente mais tarde.", {});
    }
  }

  /**
 * Realiza o download de um relatório no formato XLSX para a empresa especificada.
 * 
 * A função envia uma solicitação `POST` para o endpoint `/api/relatorio/task/download`,
 * solicitando o arquivo com base no `id` do relatório. O backend retorna o conteúdo
 * em formato binário (`blob`), que é então convertido em um arquivo e baixado no 
 * navegador com o nome `{cnpj}_relatorio.xlsx`.
 * 
 * Durante o processo, a propriedade observável `loading_download` é utilizada para
 * indicar visualmente que o download está em andamento.
 * 
 * Em caso de sucesso:
 * - O arquivo é baixado automaticamente para o usuário.
 * 
 * Em caso de falha:
 * - Se o backend retornar uma mensagem de erro no cabeçalho (`headers.msg`), ela será exibida.
 * - Caso contrário, uma mensagem genérica é exibida via `toast`.
 * 
 * @async
 * @param {string} id Identificador único do relatório a ser baixado.
 * @param {string} cnpj CNPJ da empresa que será usado no nome do arquivo.
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a tentativa de download.
 */

  async handleDownloadArquivoRelatorio(id: string, cnpj: string) {

    this.obs.loading_download = true

    try {

      let resp = await api.post(`/api/relatorio/task/download`, {id: id, format: "xlsx"}, { responseType: 'blob' });

      if(resp.status == 200){

        const fileName = cnpj + "_relatorio.xlsx";

        downloadBinaryFile(resp.data, fileName)

      } else {

        toast.error(decodeUtf8String(resp.headers.msg), {});

      }

    } catch (ex) {

      toast.error('erro ao baixar o arquivo, tente novamente mais tarde ou contate o suporte.', {});

    }

    this.obs.loading_download = false

  }

}


export const SpedApuracaoStore = new _SpedApuracaoStore();